// ---------- énumérations ----------
export const XimiApp = { Famille: 'FAMILLE', Mobile: 'MOBILE' } as const;
export type XimiApp = (typeof XimiApp)[keyof typeof XimiApp];

export const XimiEnv = {
    Dev: 'dev',
    Qa: 'qa',
    Uat: 'uat',
    Test: 'test',
    Prod: 'prod'
} as const;
export type XimiEnv = (typeof XimiEnv)[keyof typeof XimiEnv];

// Env codes that appear as a URL prefix. Prod has NO prefix (default), and we
// deliberately exclude it here — otherwise a non-env first segment could collide
// (e.g. appId "Demo"). Mirrors Ximi Mobile's list ['uat','qa','test','dev'].
const URL_ENV_CODES: readonly XimiEnv[] = [XimiEnv.Dev, XimiEnv.Qa, XimiEnv.Uat, XimiEnv.Test];

// ---------- interne ----------
const MOBILE_PATH_PREFIX = '/ximi/start/';

const decodeSegment = (segment: string): string => {
    const padded = segment + '='.repeat((4 - (segment.length % 4)) % 4);
    return new TextDecoder().decode(Uint8Array.from(atob(padded), (c) => c.charCodeAt(0)));
};

const toXimiEnv = (value: string | undefined): XimiEnv | null => {
    const lowered = value?.toLowerCase() ?? '';
    return (URL_ENV_CODES as readonly string[]).includes(lowered) ? (lowered as XimiEnv) : null;
};

// ---------- 1) type d'app + URL épurée ----------
export interface XimiCleanUrl {
    app: XimiApp;
    cleanUrl: string; // segments base64 uniquement (host + /ximi/start/[_/] retirés)
}

/**
 * Détecte le format du lien et renvoie l'URL épurée (segments base64 seuls) :
 * - Mobile : `m.xelya.io/ximi/start/[_/]<b64…>` → on retire `/ximi/start/` + `_/` (SSO)
 * - Famille : `<host>/<b64…>` → on garde le pathname
 */
export function getXimiCleanUrl(rawUrl: string): XimiCleanUrl | null {
    let parsed: URL;
    try {
        parsed = new URL(rawUrl);
    } catch {
        return null;
    }
    const trim = (path: string) => path.replace(/^\/+|\/+$/g, '');

    if (parsed.pathname.startsWith(MOBILE_PATH_PREFIX)) {
        let rest = parsed.pathname.slice(MOBILE_PATH_PREFIX.length);
        if (rest.startsWith('_/')) rest = rest.slice(2); // marqueur SSO
        return { app: XimiApp.Mobile, cleanUrl: trim(rest) };
    }
    return { app: XimiApp.Famille, cleanUrl: trim(parsed.pathname) };
}

// ---------- 2) données complètes d'un lien Ximi (QR ou deeplink) ----------
export interface XimiData {
    app: XimiApp;
    env: XimiEnv; // Prod par défaut si pas de préfixe env (legacy)
    appId: string;
    tenant: string;
    tenantCode: string; // `${appId}/${tenant}` → le paramètre SSO tenant-code
    loginHint: string;
    displayName?: string;
}

/**
 * Parse un lien Ximi (QR ou deeplink), Famille ou Mobile. Détecte l'env via le 1er
 * segment (sinon Prod) et décale les index en conséquence. Renvoie null si invalide.
 */
export function getXimiDataFromUrl(rawUrl: string): XimiData | null {
    const clean = getXimiCleanUrl(rawUrl);
    if (!clean) return null;

    let segments: string[];
    try {
        segments = clean.cleanUrl.split('/').filter(Boolean).map(decodeSegment);
    } catch {
        return null;
    }

    const detectedEnv = toXimiEnv(segments[0]);
    const offset = detectedEnv ? 1 : 0;
    const appId = segments[offset];
    const tenant = segments[offset + 1];
    const loginHint = segments[offset + 2];
    const displayName = segments[offset + 3];
    if (!appId || !tenant || !loginHint) return null;

    return {
        app: clean.app,
        env: detectedEnv ?? XimiEnv.Prod,
        appId,
        tenant,
        tenantCode: `${appId}/${tenant}`,
        loginHint,
        displayName
    };
}

// ---------- wrappers (mêmes points d'entrée, source-explicites) ----------
/** URL lue depuis un QR scanné. */
export const parseXimiProvisioningFromScanner = (scannedValue: string): XimiData | null =>
    getXimiDataFromUrl(scannedValue);

/** URL ouverte depuis un deeplink de provisioning (email/SMS). */
export const parseXimiProvisioningFromDeeplink = (deeplinkUrl: string): XimiData | null =>
    getXimiDataFromUrl(deeplinkUrl);

// ---------- 3) saisie manuelle (tenant-code + identifiant tapés à la main) ----------
export const XimiManualEntryError = {
    TenantCodeRequired: 'TENANT_CODE_REQUIRED',
    LoginRequired: 'LOGIN_REQUIRED',
    TenantCodeFormat: 'TENANT_CODE_FORMAT', // doit faire 2 ou 3 parties séparées par /
    UnknownEnv: 'UNKNOWN_ENV' // 3 parties mais 1ʳᵉ partie non reconnue comme env
} as const;
export type XimiManualEntryError = (typeof XimiManualEntryError)[keyof typeof XimiManualEntryError];

export interface XimiManualEntry {
    tenantCode: string; // app/tenant (env retiré si fourni) → paramètre SSO tenant-code
    loginHint: string;
}

// Erreurs par champ : chaque champ peut échouer indépendamment (les deux à la fois
// si les deux sont vides) → la page affiche le message sous l'input concerné.
export interface XimiManualEntryErrors {
    tenantCode?: XimiManualEntryError;
    login?: XimiManualEntryError;
}

export type XimiManualEntryResult =
    | { ok: true; entry: XimiManualEntry }
    | { ok: false; errors: XimiManualEntryErrors };

// Env acceptés en saisie manuelle (1ʳᵉ partie quand 3 parties). Prod inclus ici
// — contrairement à URL_ENV_CODES — car l'utilisateur le saisit explicitement.
const MANUAL_ENV_CODES: readonly string[] = Object.values(XimiEnv);

type TenantCodeParse = { error: XimiManualEntryError } | { tenantCode: string };

/** Valide le code organisme seul → erreur (par champ) ou `app/tenant` (env retiré). */
const parseTenantCode = (rawTenantCode: string): TenantCodeParse => {
    const trimmed = rawTenantCode.trim();
    if (!trimmed) return { error: XimiManualEntryError.TenantCodeRequired };

    const parts = trimmed
        .split('/')
        .map((part) => part.trim())
        .filter(Boolean);
    if (parts.length !== 2 && parts.length !== 3) {
        return { error: XimiManualEntryError.TenantCodeFormat };
    }
    if (parts.length === 3 && !MANUAL_ENV_CODES.includes(parts[0].toLowerCase())) {
        return { error: XimiManualEntryError.UnknownEnv };
    }

    const [app, tenant] = parts.slice(-2); // 2 dernières parties = app/tenant
    return { tenantCode: `${app}/${tenant}` };
};

/**
 * Valide + transforme une saisie manuelle (code organisme 2 ou 3 parties + identifiant).
 * Renvoie une erreur PAR CHAMP (les deux peuvent être présentes simultanément).
 * Toute la logique de contrôle vit ici — la page ne fait qu'afficher les messages.
 */
export function parseXimiManualEntry(
    rawTenantCode: string,
    rawLogin: string
): XimiManualEntryResult {
    const tenant = parseTenantCode(rawTenantCode);
    const loginHint = rawLogin.trim();

    if ('error' in tenant || !loginHint) {
        return {
            ok: false,
            errors: {
                tenantCode: 'error' in tenant ? tenant.error : undefined,
                login: loginHint ? undefined : XimiManualEntryError.LoginRequired
            }
        };
    }

    return { ok: true, entry: { tenantCode: tenant.tenantCode, loginHint } };
}
